import Vue from "vue";
import VueRouter, { RawLocation, Route, RouteConfig } from "vue-router";
import { AuthService } from "@/services/auth.service";
import { AuthModule } from "@/store/auth.module";

// catching errors
const originalPush = VueRouter.prototype.push as (
  location: RawLocation,
) => Promise<Route>;
VueRouter.prototype.push = async function (location: RawLocation) {
  return originalPush.call(this, location).catch(err => {
    if (err.name == "NavigationDuplicated") return err;
    throw err;
  });
};

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "/",
    name: "",
    meta: {
      isPublic: false,
    },
    redirect: { name: "f-redirect" },
    component: () => import("@/views/App/AppMain.vue"),
    children: [
      {
        path: "main",
        name: "main",
        meta: {
          isPublic: false,
        },
        component: () => import("@/views/Menu/MenuView.vue"),
        children: [
          {
            path: "/accounts",
            name: "account-list",
            meta: {
              isPublic: false,
            },
            component: () => import("@/views/App/Main.vue"),
          },
          {
            path: "/account-info/:id",
            name: "account-info",
            meta: {
              isPublic: false,
            },
            component: () => import("@/views/App/AccountInfo.vue"),
          },
          {
            path: "/service-info/:id",
            name: "service-info",
            meta: {
              isPublic: false,
            },
            component: () => import("@/views/App/ServiceInfo.vue"),
          },
          {
            path: "/calculate/:id",
            name: "calculate",
            meta: {
              isPublic: false,
            },
            component: () => import("@/views/App/Calculate.vue"),
          },
          {
            path: "/pays/:id",
            name: "pays",
            meta: {
              isPublic: false,
            },
            component: () => import("@/views/App/Pays.vue"),
          },
          {
            path: "/invoices/:id",
            name: "invoices",
            meta: {
              isPublic: false,
            },
            component: () => import("@/views/App/Invoices.vue"),
          },
          {
            path: "/add-account/",
            name: "add-account",
            meta: {
              isPublic: false,
            },
            component: () => import("@/views/App/AddAccount.vue"),
          },
          {
            path: "/change-pwd/",
            name: "change-pwd",
            meta: {
              isPublic: false,
            },
            component: () => import("@/views/App/ChangePassword.vue"),
          },
          {
            path: "/change-email/",
            name: "change-email",
            meta: {
              isPublic: false,
            },
            component: () => import("@/views/App/ChangeEmail.vue"),
          },
          {
            path: "/change-personal/",
            name: "change-personal",
            meta: {
              isPublic: false,
            },
            component: () => import("@/views/App/ChangePersonal.vue"),
          },
        ],
      },
      {
        name: "f-redirect",
        path: "redirect",
        component: () => import("@/views/FRedirect.vue"),
      },
    ],
  },
  {
    path: "/auth",
    name: "auth",
    component: () => import("@/views/Auth/AuthView.vue"),
    meta: {
      isPublic: true,
      onlyWhenLoggedOut: true,
    },
  },
  {
    path: "/verify",
    name: "verify",
    component: () => import("@/views/FVerify.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/ForgotPassword",
    name: "ForgotPassword",
    component: () => import("@/views/Auth/ForgotPassword.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/updatePassword",
    name: "updatePassword",
    component: () => import("@/views/Auth/UpdatePassword.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/restorePassword",
    name: "restorePassword",
    component: () => import("@/views/Auth/UpdatePassword.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/warningRestore",
    name: "warningRestore",
    component: () => import("@/views/Auth/WarningRestore.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/passChanged",
    name: "passChanged",
    component: () => import("@/views/Auth/PassChanged.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/registration",
    name: "registration",
    component: () => import("@/views/Auth/Registration.vue"),
    meta: {
      isPublic: true,
    },
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(async (to, from, next) => {
  const hasToken = AuthService.hasToken();
  const isPublic = to.matched.some(record => record.meta.isPublic);

  if (
    hasToken &&
    to.path &&
    (to.path.toLowerCase().indexOf("getdoc") > 0 ||
      to.path.indexOf("updatePassword") > 0 ||
      to.path.indexOf("restorePassword") > 0 ||
      to.path.indexOf("verify") > 0)
  ) {
    AuthService.logout(false);
    return next({
      path: to.fullPath,
    });
  }

  if (hasToken && (to.path == "/auth" || !to.name)) {
    await AuthModule.getUser();
    if (AuthModule.user) {
      return next({ path: "/main" });
    }
    AuthService.logout(false);
    return next({ path: "/auth" });
  }

  if (!hasToken && !isPublic) {
    return next({
      path: "/auth",
    });
  }

  if (hasToken && isPublic) {
    return next({
      path: "/",
    });
  }

  next();
});

export default router;
